import styles from '../../Home.module.less'
import * as uuid from 'uuid'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    DecisionStatusType,
    FeedbackFilterType,
    SearchByIdOptionType,
    ResultType,
} from 'Types/Tasks.types.ts'
import ConfigProvider from 'antd/lib/config-provider'
import Input from 'antd/lib/input'
import Select from 'antd/lib/select'
import Flex from 'antd/lib/flex'
import Space from 'antd/lib/space'
import Tooltip from 'antd/lib/tooltip'
import EyeFilled from '@ant-design/icons/lib/icons/EyeFilled'
import CopyButton from 'Components/common/CopyButton/CopyButton.tsx'
import { debounce } from 'lodash'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import TableComp from 'Components/common/Table/Table'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
    getTasksOutputByJob,
    getUserJobHistory,
    getUserProjectHistory,
} from 'Services/tasks.service'
import dayjs from 'dayjs'
import { getValueFromOldOrNewValueTypes } from 'Utils/common.utils'
import { notification, Table, TableColumnsType } from 'antd/lib'

const allowedActions = ['preview', 'copy']

export type TaskFeedbackDataType = {
    key: string
    taskId: string
    feedbackBy: string
    comment: string | undefined
    submittedAt: string | undefined
    result: string
    rejectionReasons?: string[]
}

export type JobFeedbackDataType = {
    key: string
    jobId: string
    role: string
    submittedAt: string
}

export const FeedbackTab: React.FC = () => {
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10 })
    const [filters, setFilters] = useState<FeedbackFilterType>({})
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null)
    const [searchByIdOption, setSearchByIdOption] = useState<SearchByIdOptionType>('jobId')
    const [api, contextHolder] = notification.useNotification()

    const userJobHistoryPayload = useMemo(() => {
        const basePayload = {
            offset: pagination.pageSize * (pagination.page - 1),
            limit: pagination.pageSize,
            workflowId: filters.workflowId,
            projectId: filters.projectId,
        }

        if (searchByIdOption === 'jobId' && filters.id !== undefined) {
            return { ...basePayload, jobId: filters.id }
        } else if (searchByIdOption === 'taskId' && filters.id !== undefined) {
            return { ...basePayload, taskId: filters.id }
        }

        return basePayload
    }, [
        filters.id,
        filters.projectId,
        filters.workflowId,
        pagination.page,
        pagination.pageSize,
        searchByIdOption,
    ])

    const isFetchingTaskWithValidId =
        searchByIdOption === 'taskId' && uuid.validate(filters.id ?? '')

    const {
        data: jobData,
        isLoading: isJobDataLoading,
        error: userJobHistoryError,
    } = useQuery({
        queryKey: ['userJobHistory', { userJobHistoryPayload }],
        queryFn: () => getUserJobHistory(userJobHistoryPayload),
        enabled: filters.id ? uuid.validate(filters.id) : true,
        placeholderData: keepPreviousData,
    })

    const {
        data: projectsData,
        isLoading: isProjectsDataLoading,
        error: userProjectHistoryError,
    } = useQuery({
        queryKey: ['userProjectHistory'],
        queryFn: () => getUserProjectHistory(),
    })

    const {
        data: tasksData,
        isLoading: isTasksDataLoading,
        error: tasksOutputError,
    } = useQuery({
        queryKey: ['tasksOutput', expandedRowKey],
        queryFn: () => getTasksOutputByJob(expandedRowKey!),
        enabled: !!expandedRowKey,
    })

    useEffect(() => {
        if (userJobHistoryError) {
            api.error({
                message: userJobHistoryError?.message ?? 'Error in loading jobs',
                description: 'Failed to load jobs on which feedback was given.',
                placement: 'bottomLeft',
            })
        }
    }, [api, userJobHistoryError])

    useEffect(() => {
        if (userProjectHistoryError) {
            api.error({
                message: userProjectHistoryError?.message ?? 'Error in loading projects',
                description: 'Failed to load projects',
                placement: 'bottomLeft',
            })
        }
    }, [api, userProjectHistoryError])

    useEffect(() => {
        if (tasksOutputError) {
            api.error({
                message: tasksOutputError?.message ?? 'Error in loading tasks of the job',
                description: 'Failed to load tasks of the job',
                placement: 'bottomLeft',
            })
        }
    }, [api, tasksOutputError])

    useEffect(() => {
        if (jobData && isFetchingTaskWithValidId && jobData?.jobs.length) {
            setExpandedRowKey(jobData?.jobs[0].jobId)
        }
    }, [isFetchingTaskWithValidId, jobData, tasksData])

    const taskFeedbackColumns: TableColumnsType<TaskFeedbackDataType> = useMemo(
        () => [
            {
                title: 'Feedback by',
                width: 200,
                dataIndex: 'feedbackBy',
                key: 'feedbackBy',
                className: styles['feedback-td'],
            },
            {
                title: 'Feedback & Comment',
                dataIndex: 'comment',
                key: 'comment',
                className: styles['feedback-td'],
                render: (comment: string | undefined, record: TaskFeedbackDataType) => {
                    const hasRejectionReasons = !!record.rejectionReasons?.length
                    const isReviewer = record.feedbackBy.toLowerCase().includes('reviewer')

                    if (!isReviewer) {
                        return comment ?? '--'
                    }

                    if (!comment && !hasRejectionReasons) {
                        return '--'
                    }

                    return (
                        <div className={styles['feedback-content']}>
                            {hasRejectionReasons && (
                                <div className={styles['feedback-section']}>
                                    <div className={styles['feedback-header']}>
                                        Reasons for rejection:
                                    </div>
                                    <ol>
                                        {record.rejectionReasons?.map((reason, index) => (
                                            <li key={index}>{reason}</li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                            {comment && (
                                <div className={styles['feedback-section']}>
                                    {hasRejectionReasons && (
                                        <div className={styles['feedback-header']}>
                                            Additional comments:
                                        </div>
                                    )}
                                    <div>{comment}</div>
                                </div>
                            )}
                        </div>
                    )
                },
            },
            {
                title: 'Date',
                width: 180,
                dataIndex: 'submittedAt',
                key: 'date',
                render: (submittedAt: string | undefined) =>
                    submittedAt ? dayjs(submittedAt).format('HH:mm, Do MMM YYYY') : '--',
                className: styles['feedback-td'],
            },
            {
                title: 'Result',
                width: 140,
                dataIndex: 'result',
                render: result => {
                    let className = ''
                    let text = result
                    if ([DecisionStatusType.ACCEPT, ResultType.PASS].includes(text)) {
                        className = 'result-pass'
                        if (text === DecisionStatusType.ACCEPT) text = 'ACCEPTED'
                    } else if ([ResultType.FAIL, DecisionStatusType.REJECT].includes(text)) {
                        if (text === DecisionStatusType.REJECT) text = 'REJECTED'
                        className = 'result-fail'
                    }
                    return <span className={styles[className]}>{text}</span>
                },
            },
            {
                title: 'Action',
                width: 120,
                render: (data: TaskFeedbackDataType) => {
                    return (
                        <Space size={'large'}>
                            {allowedActions.map(action => {
                                switch (action) {
                                    case 'preview':
                                        return (
                                            <Tooltip
                                                key={data.taskId + action}
                                                title="View Submission"
                                            >
                                                <EyeFilled
                                                    role="button"
                                                    onClick={() => {
                                                        window.open(
                                                            `/preview-task/${data.taskId}`,
                                                            '_blank',
                                                            'noopener noreferrer'
                                                        )
                                                    }}
                                                    className={styles['actionIcon']}
                                                />
                                            </Tooltip>
                                        )
                                    case 'copy':
                                        return (
                                            <div
                                                className={styles['action-copy-button']}
                                                key={data.taskId + action}
                                            >
                                                <CopyButton
                                                    contentToCopy={data.taskId}
                                                    showTooltip={true}
                                                    copySuccessMessage={'TaskId Copied'}
                                                />
                                            </div>
                                        )
                                }
                            })}
                        </Space>
                    )
                },
            },
        ],
        []
    )

    const toggleExpand = useCallback((key: string) => {
        setExpandedRowKey(value => (key === value ? null : key))
    }, [])

    const expandDataSource: TaskFeedbackDataType[] | undefined = useMemo(() => {
        let data = tasksData?.taskOutput.map(task => {
            const thoughtComment = getValueFromOldOrNewValueTypes(
                task.output.decision?.supporting_comment
            )
            const supplementComment = getValueFromOldOrNewValueTypes(
                task.output?.supplement?.comment
            )

            let thoughtMessage
            let status: string = task.result
            if (thoughtComment) {
                thoughtMessage = thoughtComment
            } else if (supplementComment) {
                thoughtMessage = supplementComment
            }

            if (!status) {
                status = task.output.decision?.status ?? ''
            }

            return {
                key: task.taskId,
                taskId: task.taskId,
                feedbackBy: `${task.stepName.toUpperCase()}${
                    task.isCurrentUserStep ? ' (you)' : ''
                }`,
                submittedAt: task.output.updated_at,
                comment: thoughtMessage,
                result: status,
                rejectionReasons: task.output.decision?.submission_reasons,
            }
        })
        if (searchByIdOption === 'taskId' && filters.id) {
            data = data?.filter(row => row.taskId === filters.id)
        }
        return data
    }, [filters.id, searchByIdOption, tasksData?.taskOutput])

    const expandedRowRender = useCallback(
        () => (
            <ConfigProvider
                theme={{
                    components: {
                        Table: {
                            headerBg: '#F2EFF4',
                            headerBorderRadius: 0,
                            headerColor: '#7C53A5',
                            headerSplitColor: '#D8CBE5',
                            rowExpandedBg: '#FF0000',
                        },
                    },
                }}
            >
                <Table<TaskFeedbackDataType>
                    loading={isTasksDataLoading}
                    columns={taskFeedbackColumns}
                    dataSource={expandDataSource}
                    pagination={false}
                    size="middle"
                    rowHoverable={false}
                    scroll={{
                        y: 'calc(100vh - 600px)',
                    }}
                />
            </ConfigProvider>
        ),
        [expandDataSource, isTasksDataLoading, taskFeedbackColumns]
    )

    const columns = useMemo<TableColumnsType<JobFeedbackDataType>>(
        () => [
            {
                title: 'Job ID',
                dataIndex: 'jobId',
                key: 'jobId',
                className: styles['feedback-td'],
            },
            {
                title: 'Your Role',
                dataIndex: 'role',
                key: 'role',
                width: 200,
                className: styles['feedback-td'],
            },
            {
                title: 'Submitted at',
                dataIndex: 'submittedAt',
                key: 'submittedAt',
                className: styles['feedback-td'],
                render: (submittedAt: string) => {
                    if (!submittedAt) {
                        return <span>N/A</span>
                    }
                    return dayjs(submittedAt).format('HH:mm, Do MMM YYYY')
                },
            },
            {
                title: 'View Feedback',
                width: 250,
                render: record => {
                    return (
                        <button
                            className={styles['view-feedback']}
                            onClick={() => toggleExpand(record.key)}
                        >
                            <Flex gap={10} align="center" justify="center">
                                View{' '}
                                {expandedRowKey === record.key ? <UpOutlined /> : <DownOutlined />}
                            </Flex>
                        </button>
                    )
                },
            },
        ],
        [expandedRowKey, toggleExpand]
    )

    const handleSearchOptionChange = useCallback((option: SearchByIdOptionType, value?: string) => {
        setSearchByIdOption(option)
        if (value) {
            setFilters(prev => ({ ...prev, id: value }))
        }
    }, [])

    const dataSource = useMemo(
        () =>
            jobData?.jobs.map<JobFeedbackDataType>(job => ({
                key: job.jobId,
                jobId: job.jobId,
                role: job.role,
                submittedAt: job.submittedAt,
            })),
        [jobData?.jobs]
    )

    const handleSearchChange = debounce((value: string) => {
        setFilters({ id: value || undefined })
    }, 500)

    const filterBySearchOptions = useMemo(
        () => (
            <Select<SearchByIdOptionType>
                defaultValue="jobId"
                onChange={val => handleSearchOptionChange(val, filters?.id)}
            >
                <Select.Option value="jobId">Job ID</Select.Option>
                <Select.Option value="taskId">Task ID</Select.Option>
            </Select>
        ),
        [filters?.id, handleSearchOptionChange]
    )

    const projectFilterOptions = useMemo(() => {
        if (projectsData) {
            return projectsData.projects.map(project => ({
                key: project.projectId,
                label: project.projectName,
                value: project.projectId,
            }))
        }
        return []
    }, [projectsData])

    const workflowFilterOptions = useMemo(() => {
        if (projectsData) {
            const project = projectsData.projects.find(
                project => project.projectId === filters.projectId
            )
            if (project) {
                return project.workflows.map(workflow => ({
                    key: workflow.id,
                    label: workflow.name,
                    value: workflow.id,
                }))
            }
        }
        return []
    }, [filters.projectId, projectsData])

    const onUpdatePagination = useCallback((page: number, pageSize: number) => {
        setPagination({ page, pageSize })
    }, [])

    return (
        <Flex vertical gap={24} className={styles['history-tab']} data-testid="history-tab">
            {contextHolder}
            <Flex gap={16}>
                <Input
                    data-testid="search-input"
                    placeholder="Search by ID"
                    className={styles['search-input']}
                    addonBefore={filterBySearchOptions}
                    onChange={e => {
                        handleSearchChange(e.target.value)
                    }}
                    allowClear
                    value={filters.id}
                />
                <Select
                    options={projectFilterOptions}
                    loading={isProjectsDataLoading}
                    placeholder="Filter by project"
                    popupMatchSelectWidth={false}
                    allowClear
                    value={filters?.projectId}
                    onChange={(value: string) =>
                        setFilters(prev => ({ ...prev, projectId: value, workflowId: undefined }))
                    }
                    disabled={!!filters.id}
                    style={{ width: 201 }}
                />
                <Select
                    options={workflowFilterOptions}
                    placeholder="Filter by workflow"
                    popupMatchSelectWidth={false}
                    allowClear
                    value={filters?.workflowId}
                    onChange={(value: string) =>
                        setFilters(prev => ({ ...prev, workflowId: value }))
                    }
                    style={{ width: 201 }}
                    disabled={!filters.projectId}
                />
            </Flex>
            <div className={styles['job-history-container']}>
                <TableComp<JobFeedbackDataType>
                    data-testid="history-table"
                    loading={isJobDataLoading}
                    columns={columns}
                    dataSource={dataSource}
                    expandable={{
                        expandedRowKeys: expandedRowKey ? [expandedRowKey] : undefined,
                        expandedRowRender,
                        showExpandColumn: false,
                        onExpand: () => {},
                    }}
                    pagination={{
                        pageSizeOptions: [10, 20, 50],
                        total: jobData?.count ?? 0,
                        showSizeChanger: true,
                        current: pagination.page,
                        pageSize: pagination.pageSize,
                        onChange: onUpdatePagination,
                        position: ['bottomCenter'],
                        hideOnSinglePage: true,
                    }}
                    scroll={{
                        y: 'calc(100vh - 385px)',
                    }}
                />
            </div>
        </Flex>
    )
}
